import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { getCardIconClass } from "@/modules/billing/iconClasses.tsx";
import { PrimaryCardBlockInfo } from "@/modules/billing/paymentMethodLogic.tsx";
import { mcn } from "@/utils/baseProps.ts";
import { Trans } from "@lingui/react/macro";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import type { ReactElement } from "react";

function getIconClass(account: EBillingAccount, primaryColor = "text-primary") {
    if (account.status === "PENDING_VALIDATION") {
        return "jp-information-icon mask-icon text-warning";
    }

    if (!account.isOpen) {
        return "jp-wrong-icon mask-icon text-error";
    }

    const { payType } = account;
    switch (payType) {
        case "card":
            return getCardIconClass(account.activeCard);
        case "invoice":
            return `jp-icon-text-editor mask-icon ${primaryColor}`;
        case "invoice_deactive":
        case "invoice_pending":
            return "jp-icon-text-editor mask-icon text-warning";
        case "prepay":
            // TODO: Appropriate icon?
            return `jp-wallet-icon mask-icon ${primaryColor}`;
        default:
            exhaustiveSwitchCheck(payType);
    }
}

export function BillingAccountIcon({
    account,
    primaryColor,
    ...props
}: {
    account: EBillingAccount;
    primaryColor?: string;
}) {
    return <span {...mcn(`size-1rem inline-block ${getIconClass(account, primaryColor)}`, props)} />;
}

function PayTypeInfo({ account }: { account: EBillingAccount }): ReactElement {
    const { payType } = account;
    switch (payType) {
        case "card": {
            const { activeCard } = account;
            if (!activeCard) {
                return (
                    <span className="text-error">
                        <Trans>No active card</Trans>
                    </span>
                );
            }
            return <PrimaryCardBlockInfo card={activeCard} />;
        }
        case "invoice":
            return (
                <span>
                    <Trans>Paying by invoice</Trans>
                </span>
            );
        case "invoice_deactive":
            return (
                <span className="text-warning">
                    <Trans>Paying by invoice (declined / suspended)</Trans>
                </span>
            );
        case "invoice_pending":
            return (
                <span className="text-warning">
                    <Trans>Paying by invoice (pending)</Trans>
                </span>
            );

        case "prepay":
            return (
                <span>
                    <Trans>Pre-payment</Trans>
                </span>
            );

        default:
            exhaustiveSwitchCheck(payType);
    }
}

export function ShortInfoSelect({ account }: { account: EBillingAccount }) {
    if (!account.isOpen) {
        return (
            <span className="text-error">
                <Trans>Inactive</Trans>
            </span>
        );
    }

    return <PayTypeInfo account={account} />;
}

export function ShortInfoList({ account }: { account: EBillingAccount }) {
    const { callToAction } = account;
    switch (callToAction) {
        case "add_payment_method":
            return (
                <span className="text-error">
                    <Trans>Add payment method</Trans>
                </span>
            );
        case "must_top_up":
            return (
                <span className="text-error">
                    <Trans>Top up your account</Trans>
                </span>
            );
        case "contact_support":
            return (
                <span className="text-error">
                    <Trans>Contact support</Trans>
                </span>
            );
        case "wait_for_verification":
            return (
                <span className="text-warning">
                    <Trans>Wait for verification or add card</Trans>
                </span>
            );
        case "pending":
            return (
                <span className="text-warning">
                    <Trans>Validation pending</Trans>
                </span>
            );
        case null:
            return <PayTypeInfo account={account} />;
        default:
            exhaustiveSwitchCheck(callToAction);
    }
}

// cspell:words deactive
